import type { FetchQueryParams } from '@/components/paginated-datatable/types/paginatedDataTableTypes';
import {
  CreatePartnerV1Dto,
  type PartnerFilterInnerV2Dto,
  type PartnerPaginatedV2Dto,
  type PartnerSimplePaginatedV2Dto,
  PartnersService,
  PartnerV1Dto,
  PatchPartnerV1Dto,
} from '@/openapi';
import { eventBus } from '@/configs/eventBus';

import type { PartnerSimple } from '../partnersTypes';

export function usePartners() {
  const fetchPartner = async (id: string) => {
    try {
      return await PartnersService.partnerControllerV1FindOneV1(id);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchPartnersPaginated = async ({
    filter,
    limit,
    orderBy,
    page,
    searchString,
  }: FetchQueryParams<PartnerFilterInnerV2Dto>): Promise<PartnerPaginatedV2Dto> => {
    try {
      return await PartnersService.partnerControllerV2FindAllPaginatedV2(
        orderBy,
        page,
        limit,
        filter,
        searchString,
      );
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const fetchPartnerSimplePaginated = async ({
    filter,
    limit,
    orderBy,
    page,
    searchString,
    areaId,
  }: FetchQueryParams<PartnerFilterInnerV2Dto> & {
    areaId?: string;
  }): Promise<PartnerSimplePaginatedV2Dto> => {
    try {
      return await PartnersService.partnerControllerV2FindAllSimplePaginatedV2(
        orderBy,
        page,
        limit,
        filter,
        searchString,
        areaId,
      );
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const createPartner = async (data: CreatePartnerV1Dto) => {
    try {
      await PartnersService.partnerControllerV1CreateV1(data);
      eventBus.emit('create');
    } catch (err) {
      console.log(err);
    }
  };

  const updatePartner = async (id: string, data: PatchPartnerV1Dto) => {
    try {
      await PartnersService.partnerControllerV1UpdatePartialV1(id, data);
      eventBus.emit('update');
    } catch (err) {
      console.log(err);
    }
  };

  const deletePartner = async (ids: string) => {
    try {
      await PartnersService.partnerControllerV1RemoveOneV1(ids);
      eventBus.emit('delete');
    } catch (err) {
      console.log(err);
    }
  };

  const deletePartners = async (ids: string[]) => {
    try {
      await PartnersService.partnerControllerV1RemoveManyV1({ values: ids });
      eventBus.emit('delete');
    } catch (err) {
      console.log(err);
    }
  };

  const linkPartnerToAddresses = async (partnerId: string, addressId: string) => {
    try {
      await PartnersService.partnerToAddressControllerV1LinkAddressV1(partnerId, addressId);
      eventBus.emit('update');
    } catch (err) {
      console.log(err);
    }
  };

  const deletePartnerAddress = async (partnerId: string, addressId: string) => {
    try {
      await PartnersService.partnerToAddressControllerV1UnlinkAddressV1(partnerId, addressId);
      eventBus.emit('delete');
    } catch (err) {
      console.log(err);
    }
  };

  const deletePartnerAddresses = async (partnerId: string, addressIds: string[]) => {
    try {
      await PartnersService.partnerToAddressControllerV1RemoveManyAddressesV1(partnerId, {
        values: addressIds,
      });
      eventBus.emit('delete');
    } catch (err) {
      console.log(err);
    }
  };

  return {
    createPartner,
    deletePartner,
    deletePartnerAddress,
    deletePartnerAddresses,
    deletePartners,
    fetchPartner,
    fetchPartnerSimplePaginated,
    fetchPartnersPaginated,
    linkPartnerToAddresses,
    updatePartner,
  };
}

export function usePartnersConverter() {
  const convertPartnerDtoToPartnerSimple = (data: PartnerV1Dto): PartnerSimple => {
    const { createdAt: _createdAt, modifiedAt: _modifiedAt, ...res } = data;
    return res;
  };

  const convertPartnerSimpleToCreatePartnerDto = (data: PartnerSimple): CreatePartnerV1Dto => {
    const {
      version: _version,
      id: _id,
      contactPersonToPartner: _contactPersonToPartner,
      bankingAccounts: _bankingAccounts,
      addresses: _addresses,
      ...res
    } = data;
    return res;
  };

  const convertPartnerSimpleToUpdatePartnerDto = (data: PartnerSimple): PatchPartnerV1Dto => {
    const {
      id: _id,
      contactPersonToPartner: _contactPersonToPartner,
      bankingAccounts: _bankingAccounts,
      addresses: _addresses,
      ...res
    } = data;
    return res;
  };

  return {
    convertPartnerDtoToPartnerSimple,
    convertPartnerSimpleToCreatePartnerDto,
    convertPartnerSimpleToUpdatePartnerDto,
  };
}
